import { Auth, I18n } from 'aws-amplify';
import { Formik, FormikHelpers } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import UsersClient from 'src/clients/UsersClient';
import { ContextProps, RouteContext } from 'src/context';
import { objectWithProperties } from 'src/legacy/components/AwsAmplify/common/constants';
import RowDivider from 'src/legacy/components/RowDivider';
import MemoBaseTypography from 'src/legacy/components/Text/BaseTypography';
import { SharpOutlinedTextField } from 'src/legacy/components/TextField';
import { GoogleAuthButton } from 'src/legacy/components/UI/Buttons/GoogleAuthButton';
import { PasswordInputValidation, SignInWithGoogle } from 'src/utils/AuthUtils';
import { removeSpaceFromString } from 'src/utils/StringUtils';

import { OnboardingAuthLayoutProps } from '../Login/OnboardingAuthLayout';
import OnboardingButton from '../OnboardingButton';
import { getFormErrorText } from '../utils';

import { OnboardingLoginFormFooterActions } from './OboardingLoginFormFooterActions';
import { OnboardingFormError } from './OnboardingFormError';

const schema = Yup.object()
  .strict(true)
  .shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: PasswordInputValidation,
  });

interface FormValues {
  email: string;
  password: string;
}

export interface OnboardingRegisterProps extends OnboardingAuthLayoutProps {
  authUser: Record<any, any> | null;
}

export const OnboardingRegisterForm: React.FC<OnboardingRegisterProps> = ({
  authUser,
  onAuthComplete,
}) => {
  const context: ContextProps = React.useContext(RouteContext);
  const initialEmail = context?.query?.email || '';

  // custom state is used to redirect the user after succuessfull login
  // this will add a action param to the login page, which triggers the
  // check for logged in user and completes the login flow
  // this is done for an activate account page, so we make sure we have a portal to
  // login in to which is the next param
  const customState =
    context?.query?.next &&
    `/login?${new URLSearchParams({
      ...context.query,
      action: 'confirm-login',
    } as Record<string, string>).toString()}`;

  const initialValues: FormValues = {
    email: initialEmail || '',
    password: '',
  };

  const handleActivateAccount = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    const user = authUser;
    setSubmitting(true);
    try {
      const { password } = values;

      if (!user || !password) return;

      const { requiredAttributes } = user.challengeParam;
      const attrs = objectWithProperties(values, requiredAttributes);

      const res = await Auth.completeNewPassword(user, password, attrs);
      setSubmitting(false);
      if (res) {
        await UsersClient.setAppUserCookie(
          res,
          'workspace_login',
          'workspaces',
          values.email,
        );
      }
      onAuthComplete();
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error?.message || 'Unexpected Error');
      }
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleActivateAccount}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          isSubmitting,
        }) => (
          <form noValidate onSubmit={handleSubmit} data-testid="testingform">
            <GoogleAuthButton
              onClick={() => SignInWithGoogle(customState || 'signup')}
              noBorderRadius
            />
            <RowDivider mt={3.25} mb={0.75}>
              <MemoBaseTypography fontType="10Medium">OR</MemoBaseTypography>
            </RowDivider>
            <SharpOutlinedTextField
              sizeVariant="tall"
              id="login-username"
              fullWidth
              type="text"
              InputProps={{
                'data-testid': 'register-emailInput',
              }}
              key="email"
              name="email"
              variant="outlined"
              onBlur={handleBlur}
              onInput={handleChange}
              value={values.email ? removeSpaceFromString(values.email) : ''}
              label={I18n.get('Work email')}
              placeholder={I18n.get('Work email')}
              error={Boolean(touched.email && errors.email)}
              helperText={(touched.email && errors.email) || ' '}
              autoComplete="off"
              disabled={Boolean(initialValues.email)}
            />
            <SharpOutlinedTextField
              sizeVariant="tall"
              id="login-password"
              fullWidth
              type="password"
              InputProps={{
                'data-testid': 'register-passwordInput',
              }}
              key="password"
              name="password"
              variant="outlined"
              onBlur={handleBlur}
              onInput={handleChange}
              value={values.password}
              label={I18n.get('Set a password')}
              error={Boolean(touched.password && errors.password)}
              autoComplete="off"
              placeholder={I18n.get('Set a password')}
            />

            <OnboardingFormError
              errorText={getFormErrorText(errors, touched)}
            />

            <OnboardingButton
              type="submit"
              isLoading={isSubmitting}
              data-testid="submit-button"
              color="primary"
              variant="contained"
              fullWidth
              htmlId="create-account-submit-button"
            >
              Create account
            </OnboardingButton>
          </form>
        )}
      </Formik>
      <OnboardingLoginFormFooterActions showOnboardingLoginActions={false} />
    </>
  );
};
